import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiList } from '../api';
import * as XLSX from 'xlsx';

function Reports() {
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [monthlyReport, setMonthlyReport] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [users, setUsers] = useState([]);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [reportType, setReportType] = useState('daily');
  const [searchText, setSearchText] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportType]);

  const fetchJsonSafe = async (path) => {
    try {
      const response = await apiFetch(path);

      if (!response || !response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`Error loading ${path}:`, error);
      return null;
    }
  };

  const fetchReports = async () => {
    try {
      setLoading(true);
      setError('');

      const endpoint = reportType === 'daily' ? '/dashboard' : '/dashboard/monthly';

      const [
        dashboardData,
        transactionsData,
        storesData,
        walletsData,
        usersData
      ] = await Promise.all([
        fetchJsonSafe(endpoint),
        fetchJsonSafe('/transactions'),
        fetchJsonSafe('/stores'),
        fetchJsonSafe('/wallets'),
        fetchJsonSafe('/users')
      ]);

      if (reportType === 'daily') {
        setReport(dashboardData?.dashboard || dashboardData || null);
      } else {
        setMonthlyReport(dashboardData?.monthly_dashboard || dashboardData || null);
      }

      setTransactions(getApiList(transactionsData, 'transactions'));
      setStores(getApiList(storesData, 'stores'));
      setWallets(getApiList(walletsData, 'wallets'));
      setUsers(getApiList(usersData, 'users'));
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء جلب بيانات التقارير');
    } finally {
      setLoading(false);
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
      return '0';
    }

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const formatDate = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '-';

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن',
      collection: 'قبض',
      transfer: 'تحويل',
      expense: 'مصروف',
      company_settlement: 'تسديد شركة'
    };

    return labels[type] || type || '-';
  };

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄',
      collection: '💵',
      transfer: '↔️',
      expense: '💸',
      company_settlement: '🏢'
    };

    return icons[type] || '💳';
  };

  const getStoreName = (storeId) => {
    if (!storeId) return '-';
    return stores.find(store => store.id === storeId)?.name || String(storeId).slice(0, 8);
  };

  const getWalletName = (walletId) => {
    if (!walletId) return '-';
    return wallets.find(wallet => wallet.id === walletId)?.name || String(walletId).slice(0, 8);
  };

  const getUserName = (userId) => {
    if (!userId) return '-';
    return users.find(user => user.id === userId)?.name || String(userId).slice(0, 8);
  };

  const dailyStats = report?.daily_stats || {};
  const summary = report?.summary || {};
  const topStores = report?.top_stores || [];

  const monthlyStats = monthlyReport?.stats_by_type || [];
  const dailyChart = monthlyReport?.daily_chart || [];

  const filteredTransactions = useMemo(() => {
    const query = normalizeArabic(searchText);

    return transactions
      .filter((tx) => {
        if (transactionTypeFilter === 'all') return true;
        return tx.type === transactionTypeFilter;
      })
      .filter((tx) => {
        if (!dateFrom && !dateTo) return true;

        const txTime = new Date(tx.created_at || 0).getTime();

        if (dateFrom) {
          const fromTime = new Date(`${dateFrom}T00:00:00`).getTime();
          if (txTime < fromTime) return false;
        }

        if (dateTo) {
          const toTime = new Date(`${dateTo}T23:59:59`).getTime();
          if (txTime > toTime) return false;
        }

        return true;
      })
      .map((tx) => {
        const fields = [
          tx.id,
          tx.type,
          getTypeLabel(tx.type),
          tx.amount,
          tx.description,
          tx.status,
          getStoreName(tx.store_id),
          getWalletName(tx.wallet_id),
          getUserName(tx.user_id),
          formatDateTime(tx.created_at)
        ];

        const text = normalizeArabic(fields.filter(Boolean).join(' | '));

        let score = 10;

        if (query) {
          score = 0;
          if (text.startsWith(query)) score = 100;
          else if (text.includes(query)) score = 60;
        }

        return {
          ...tx,
          score
        };
      })
      .filter(tx => tx.score > 0)
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      });
  }, [transactions, transactionTypeFilter, searchText, dateFrom, dateTo, stores, wallets, users]);

  const filteredSummary = useMemo(() => {
    const totals = {
      recharge: 0,
      collection: 0,
      expense: 0,
      transfer: 0,
      company_settlement: 0,
      all: 0,
      count: filteredTransactions.length
    };

    filteredTransactions.forEach((tx) => {
      const amount = Number(tx.amount || 0);
      totals.all += amount;

      if (totals[tx.type] !== undefined) {
        totals[tx.type] += amount;
      }
    });

    return totals;
  }, [filteredTransactions]);

  const makeDailyExportData = () => {
    return [
      { item: 'Wallet Balance', value: Number(summary.total_wallet_balance || 0) },
      { item: 'Daily Collection', value: Number(dailyStats.collection || 0) },
      { item: 'Daily Recharge', value: Number(dailyStats.recharge || 0) },
      { item: 'Daily Expenses', value: Number(dailyStats.expenses || 0) },
      { item: 'Daily Net', value: Number(dailyStats.net || 0) },
      { item: 'Total Debts', value: Number(summary.total_debts || 0) },
      { item: 'Active Stores', value: Number(summary.active_stores || 0) },
      { item: 'Total Wallets', value: Number(summary.total_wallets || 0) }
    ];
  };

  const makeMonthlyExportData = () => {
    return monthlyStats.map((item) => ({
      type: getTypeLabel(item.type),
      transactions_count: Number(item.transaction_count || 0),
      total_amount: Number(item.total_amount || 0)
    }));
  };

  const makeTransactionsExportData = () => {
    return filteredTransactions.map((tx) => ({
      id: tx.id,
      type: getTypeLabel(tx.type),
      amount: Number(tx.amount || 0),
      store: getStoreName(tx.store_id),
      wallet: getWalletName(tx.wallet_id),
      user: getUserName(tx.user_id),
      description: tx.description || '',
      status: tx.status || '',
      transfer_status: tx.transfer_status || '',
      related_user: getUserName(tx.related_user_id),
      created_at: formatDateTime(tx.created_at)
    }));
  };

  const exportToExcel = () => {
    try {
      setExporting(true);
      setError('');

      const workbook = XLSX.utils.book_new();

      const reportData = reportType === 'daily'
        ? makeDailyExportData()
        : makeMonthlyExportData();

      if (reportData.length > 0) {
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(reportData),
          reportType === 'daily' ? 'Daily Report' : 'Monthly Report'
        );
      }

      const txData = makeTransactionsExportData();
      if (txData.length > 0) {
        XLSX.utils.book_append_sheet(
          workbook,
          XLSX.utils.json_to_sheet(txData),
          'Filtered Transactions'
        );
      }

      if (workbook.SheetNames.length === 0) {
        setError('لا توجد بيانات لتصديرها');
        return;
      }

      XLSX.writeFile(
        workbook,
        `eshara-report-${reportType}-${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تصدير Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = () => {
    try {
      setExporting(true);
      setError('');

      const escapeHtml = (value) => String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

      const summaryRows = reportType === 'daily'
        ? makeDailyExportData().map(row => [row.item, row.value])
        : makeMonthlyExportData().map(row => [row.type, row.transactions_count, row.total_amount]);
      const txRows = makeTransactionsExportData().slice(0, 80);
      const reportTitle = reportType === 'daily' ? 'التقرير اليومي' : 'التقرير الشهري';
      const today = new Date().toISOString().slice(0, 10);

      const summaryHead = reportType === 'daily'
        ? ['البند', 'القيمة']
        : ['النوع', 'عدد العمليات', 'الإجمالي'];

      const html = `<!doctype html>
<html lang="ar" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(reportTitle)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      direction: rtl;
      font-family: "Tahoma", "Arial", sans-serif;
      margin: 0;
      padding: 24px;
      background: #fff7ed;
      color: #1f2937;
      line-height: 1.7;
    }
    .report-paper {
      max-width: 920px;
      margin: 0 auto;
      background: #fff;
      border: 1px solid #fed7aa;
      border-radius: 18px;
      padding: 24px;
      box-shadow: 0 12px 35px rgba(234, 88, 12, 0.12);
    }
    .header {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: flex-start;
      border-bottom: 2px solid #fed7aa;
      padding-bottom: 16px;
      margin-bottom: 18px;
    }
    h1 { margin: 0; color: #c2410c; font-size: 24px; }
    .muted { color: #6b7280; font-size: 13px; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 14px 0 24px;
      direction: rtl;
      text-align: right;
      page-break-inside: auto;
    }
    th, td {
      border: 1px solid #fed7aa;
      padding: 9px 10px;
      font-size: 12px;
      vertical-align: top;
    }
    th { background: #fff7ed; color: #9a3412; font-weight: 800; }
    tr:nth-child(even) td { background: #fffbf5; }
    .section-title { margin: 20px 0 8px; color: #9a3412; font-size: 17px; }
    .no-print { display: flex; gap: 8px; margin-bottom: 14px; }
    button {
      border: 0;
      background: #ea580c;
      color: #fff;
      border-radius: 12px;
      padding: 10px 16px;
      font-weight: 800;
      cursor: pointer;
    }
    @media print {
      body { background: #fff; padding: 0; }
      .report-paper { box-shadow: none; border: 0; border-radius: 0; max-width: none; }
      .no-print { display: none; }
      @page { size: A4; margin: 12mm; }
    }
  </style>
</head>
<body>
  <div class="report-paper">
    <div class="no-print">
      <button onclick="window.print()">طباعة / حفظ PDF</button>
    </div>
    <div class="header">
      <div>
        <h1>نظام الإشارة الإداري</h1>
        <div class="muted">${escapeHtml(reportTitle)}</div>
      </div>
      <div class="muted">تاريخ التصدير: ${escapeHtml(today)}</div>
    </div>

    <h2 class="section-title">الملخص</h2>
    <table>
      <thead><tr>${summaryHead.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
      <tbody>
        ${summaryRows.map(row => `<tr>${row.map(cell => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`).join('') || '<tr><td colspan="3">لا توجد بيانات</td></tr>'}
      </tbody>
    </table>

    <h2 class="section-title">آخر العمليات</h2>
    <table>
      <thead><tr><th>المرجع</th><th>النوع</th><th>المبلغ</th><th>المحل</th><th>المحفظة</th><th>التاريخ</th></tr></thead>
      <tbody>
        ${txRows.map(row => `<tr><td>${escapeHtml(row.id)}</td><td>${escapeHtml(row.type)}</td><td>${escapeHtml(row.amount)}</td><td>${escapeHtml(row.store)}</td><td>${escapeHtml(row.wallet)}</td><td>${escapeHtml(row.created_at)}</td></tr>`).join('') || '<tr><td colspan="6">لا توجد عمليات</td></tr>'}
      </tbody>
    </table>
  </div>
  <script>window.onload = function(){ setTimeout(function(){ window.print(); }, 250); };</script>
</body>
</html>`;

      const printWindow = window.open('', '_blank', 'width=980,height=720');
      if (!printWindow) {
        setError('المتصفح منع فتح نافذة PDF. اسمح بالنوافذ المنبثقة لهذا الموقع.');
        return;
      }
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } catch (err) {
      console.error(err);
      setError('حدث خطأ أثناء تجهيز PDF العربي');
    } finally {
      setExporting(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  const resetFilters = () => {
    setSearchText('');
    setTransactionTypeFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50" dir="rtl">
        <div className="bg-white rounded-3xl shadow p-8 text-xl font-bold text-gray-700">
          جاري تحميل التقارير...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-gradient-to-br from-gray-950 via-orange-900 to-black text-white rounded-3xl p-5 md:p-8 shadow-lg mb-6">
          <button
            onClick={() => navigate('/')}
            className="bg-white/10 hover:bg-white/20 text-white font-bold px-4 py-2 rounded-xl mb-5"
          >
            ← رجوع للرئيسية
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold">
                📈 التقارير
              </h1>

              <p className="text-orange-100 mt-3 text-sm md:text-base">
                تقارير الأداء والإحصائيات المالية والتصدير Excel / PDF
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={exportToExcel}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-300 text-white px-6 py-3 rounded-xl font-bold shadow"
              >
                📤 Excel
              </button>

              <button
                onClick={exportToPDF}
                disabled={exporting}
                className="bg-red-600 hover:bg-red-700 disabled:bg-red-300 text-white px-6 py-3 rounded-xl font-bold shadow"
              >
                📄 PDF
              </button>

              <button
                onClick={printReport}
                disabled={exporting}
                className="bg-gray-800 hover:bg-black disabled:bg-gray-300 text-white px-6 py-3 rounded-xl font-bold shadow"
              >
                🖨️ طباعة
              </button>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mb-6">
            ⚠️ {error}
          </div>
        )}

        {exporting && (
          <div className="bg-orange-50 border border-orange-200 text-orange-700 rounded-2xl px-4 py-3 mb-6">
            جاري تجهيز ملف التقرير...
          </div>
        )}

        {/* Report Type */}
        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setReportType('daily')}
              className={`py-3 px-6 rounded-xl font-bold transition ${
                reportType === 'daily'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              📅 تقرير يومي
            </button>

            <button
              onClick={() => setReportType('monthly')}
              className={`py-3 px-6 rounded-xl font-bold transition ${
                reportType === 'monthly'
                  ? 'bg-orange-600 text-white'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
            >
              📊 تقرير شهري
            </button>

            <button
              onClick={fetchReports}
              className="bg-gray-800 hover:bg-black text-white py-3 px-6 rounded-xl font-bold transition"
            >
              🔄 تحديث
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-100 text-blue-800 rounded-3xl p-5 md:p-6 mb-6">
          <h2 className="text-xl font-bold mb-2">📌 حالة مرحلة التقارير</h2>
          <p className="text-sm leading-7">
            هذه الصفحة تعتمد على بيانات السيرفر الحقيقية: لوحة التحكم، العمليات، المحلات، المحافظ، والمستخدمين.
            الفلاتر الحالية تؤثر على جدول العمليات وعلى ملفات التصدير.
          </p>
        </div>

        {/* Daily */}
        {reportType === 'daily' && (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <SummaryCard
                title="رصيد المحافظ"
                value={formatNumber(summary.total_wallet_balance)}
                color="blue"
              />

              <SummaryCard
                title="القبض اليومي"
                value={formatNumber(dailyStats.collection)}
                color="green"
              />

              <SummaryCard
                title="الشحن اليومي"
                value={formatNumber(dailyStats.recharge)}
                color="purple"
              />

              <SummaryCard
                title="الصافي اليومي"
                value={formatNumber(dailyStats.net)}
                color="orange"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <SummaryCard
                title="إجمالي المحافظ"
                value={summary.total_wallets || 0}
                color="blue"
              />

              <SummaryCard
                title="إجمالي الديون"
                value={formatNumber(summary.total_debts)}
                color="red"
              />

              <SummaryCard
                title="المحلات النشطة"
                value={summary.active_stores || 0}
                color="green"
              />
            </div>

            <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                🏪 أكثر المحلات نشاطاً اليوم
              </h2>

              {topStores.length === 0 ? (
                <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
                  لا توجد حركة للمحلات اليوم
                </div>
              ) : (
                <div className="space-y-3">
                  {topStores.map((store, idx) => (
                    <div
                      key={store.id || idx}
                      className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-2xl p-4"
                    >
                      <div className="flex items-center gap-3">
                        <span className="bg-orange-100 text-orange-800 font-bold px-3 py-1 rounded-xl">
                          #{idx + 1}
                        </span>

                        <span className="font-bold text-gray-900">
                          {store.name || '-'}
                        </span>
                      </div>

                      <div className="text-left">
                        <p className="text-green-700 font-bold">
                          {formatNumber(store.total_amount)}
                        </p>

                        <p className="text-gray-600 text-sm">
                          {store.transaction_count || 0} عملية
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Monthly */}
        {reportType === 'monthly' && (
          <>
            <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                📊 إحصائيات الشهر حسب نوع العملية
              </h2>

              {monthlyStats.length === 0 ? (
                <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
                  لا توجد عمليات مالية لهذا الشهر
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {monthlyStats.map((stat, idx) => (
                    <div
                      key={idx}
                      className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-4 border border-orange-100"
                    >
                      <p className="text-gray-700 font-bold">
                        {getTypeIcon(stat.type)} {getTypeLabel(stat.type)}
                      </p>

                      <p className="text-3xl font-bold text-orange-700 mt-2">
                        {formatNumber(stat.total_amount)}
                      </p>

                      <p className="text-gray-600 text-sm mt-1">
                        {stat.transaction_count || 0} عملية
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                📅 التوزيع اليومي خلال الشهر
              </h2>

              {dailyChart.length === 0 ? (
                <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
                  لا توجد بيانات يومية لهذا الشهر
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
                    {dailyChart.map((day, idx) => (
                      <div key={idx} className="bg-gray-50 border border-gray-100 rounded-2xl p-4">
                        <p className="font-bold text-gray-900 mb-3">
                          {formatDate(day.day)}
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <MiniValue label="شحن" value={formatNumber(day.recharge)} color="blue" />
                          <MiniValue label="قبض" value={formatNumber(day.collection)} color="green" />
                          <MiniValue label="مصروف" value={formatNumber(day.expense)} color="red" />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hidden xl:block overflow-x-auto">
                    <table className="w-full text-sm text-right">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-3 text-right text-gray-600">التاريخ</th>
                          <th className="px-4 py-3 text-right text-gray-600">شحن</th>
                          <th className="px-4 py-3 text-right text-gray-600">قبض</th>
                          <th className="px-4 py-3 text-right text-gray-600">مصروف</th>
                        </tr>
                      </thead>

                      <tbody>
                        {dailyChart.map((day, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 font-bold">
                              {formatDate(day.day)}
                            </td>

                            <td className="px-4 py-3 text-blue-600 font-bold">
                              {formatNumber(day.recharge)}
                            </td>

                            <td className="px-4 py-3 text-green-600 font-bold">
                              {formatNumber(day.collection)}
                            </td>

                            <td className="px-4 py-3 text-red-600 font-bold">
                              {formatNumber(day.expense)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {/* Transactions Search */}
        <div className="bg-white rounded-3xl shadow p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                🔎 تقرير العمليات التفصيلي
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                ابحث برقم العملية، اسم المحل، المحفظة، المستخدم، المبلغ، الوصف، أو التاريخ
              </p>
            </div>

            <button
              onClick={resetFilters}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 px-5 rounded-xl"
            >
              مسح الفلاتر
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="بحث ذكي في العمليات..."
              className="lg:col-span-2 w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <select
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="all">كل العمليات</option>
              <option value="recharge">شحن</option>
              <option value="collection">قبض</option>
              <option value="transfer">تحويل</option>
              <option value="expense">مصروف</option>
              <option value="company_settlement">تسديد شركة</option>
            </select>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 mb-6">
          <SummaryCard title="عدد النتائج" value={filteredSummary.count} color="blue" />
          <SummaryCard title="إجمالي المبالغ" value={formatNumber(filteredSummary.all)} color="green" />
          <SummaryCard title="الشحن" value={formatNumber(filteredSummary.recharge)} color="purple" />
          <SummaryCard title="القبض" value={formatNumber(filteredSummary.collection)} color="green" />
          <SummaryCard title="المصروف" value={formatNumber(filteredSummary.expense)} color="red" />
          <SummaryCard title="التحويل" value={formatNumber(filteredSummary.transfer)} color="blue" />
          <SummaryCard title="تسديد شركة" value={formatNumber(filteredSummary.company_settlement)} color="orange" />
        </div>

        <div className="bg-white rounded-3xl shadow p-5 md:p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            🧾 نتائج العمليات
          </h2>

          {filteredTransactions.length === 0 ? (
            <div className="text-center text-gray-500 py-10 bg-gray-50 rounded-2xl">
              لا توجد عمليات مطابقة
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:hidden gap-4">
                {filteredTransactions.slice(0, 100).map((tx) => (
                  <TransactionCard
                    key={tx.id}
                    tx={tx}
                    getTypeIcon={getTypeIcon}
                    getTypeLabel={getTypeLabel}
                    formatNumber={formatNumber}
                    formatDateTime={formatDateTime}
                    getStoreName={getStoreName}
                    getWalletName={getWalletName}
                    getUserName={getUserName}
                  />
                ))}
              </div>

              <div className="hidden xl:block overflow-x-auto">
                <table className="w-full text-sm text-right">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-gray-600">رقم</th>
                      <th className="px-4 py-3 text-gray-600">النوع</th>
                      <th className="px-4 py-3 text-gray-600">المبلغ</th>
                      <th className="px-4 py-3 text-gray-600">المحل</th>
                      <th className="px-4 py-3 text-gray-600">المحفظة</th>
                      <th className="px-4 py-3 text-gray-600">المستخدم</th>
                      <th className="px-4 py-3 text-gray-600">الوصف</th>
                      <th className="px-4 py-3 text-gray-600">الحالة</th>
                      <th className="px-4 py-3 text-gray-600">التاريخ</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredTransactions.slice(0, 300).map((tx) => (
                      <tr key={tx.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-xs text-gray-500">
                          #{String(tx.id).slice(0, 8)}
                        </td>

                        <td className="px-4 py-3 font-bold">
                          {getTypeIcon(tx.type)} {getTypeLabel(tx.type)}
                        </td>

                        <td className="px-4 py-3 font-bold text-green-700">
                          {formatNumber(tx.amount)}
                        </td>

                        <td className="px-4 py-3">
                          {getStoreName(tx.store_id)}
                        </td>

                        <td className="px-4 py-3">
                          {getWalletName(tx.wallet_id)}
                        </td>

                        <td className="px-4 py-3">
                          {getUserName(tx.user_id)}
                        </td>

                        <td className="px-4 py-3 max-w-[280px] truncate">
                          {tx.description || '-'}
                        </td>

                        <td className="px-4 py-3 text-xs text-gray-600">
                          {tx.transfer_status || tx.status || '-'}
                        </td>

                        <td className="px-4 py-3 text-xs text-gray-500">
                          {formatDateTime(tx.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {filteredTransactions.length > 300 && (
            <p className="text-center text-gray-500 text-sm mt-4">
              يتم عرض أول 300 عملية فقط في الجدول لتحسين الأداء. استخدم البحث أو التاريخ لتقليل النتائج.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ title, value, color }) {
  const colors = {
    blue: 'border-blue-500 text-blue-700 bg-blue-50',
    green: 'border-green-500 text-green-700 bg-green-50',
    purple: 'border-purple-500 text-purple-700 bg-purple-50',
    orange: 'border-orange-500 text-orange-700 bg-orange-50',
    red: 'border-red-500 text-red-700 bg-red-50'
  };

  return (
    <div className={`bg-white rounded-2xl shadow p-5 border-r-4 ${colors[color] || colors.blue}`}>
      <p className="text-gray-600 text-sm">{title}</p>
      <p className="text-2xl md:text-3xl font-bold mt-2">{value}</p>
    </div>
  );
}

function MiniValue({ label, value, color }) {
  const classes = {
    blue: 'text-blue-700 bg-blue-50',
    green: 'text-green-700 bg-green-50',
    red: 'text-red-700 bg-red-50'
  };

  return (
    <div className={`rounded-2xl p-3 ${classes[color] || classes.blue}`}>
      <p className="text-xs">{label}</p>
      <p className="font-bold mt-1">{value}</p>
    </div>
  );
}

function TransactionCard({
  tx,
  getTypeIcon,
  getTypeLabel,
  formatNumber,
  formatDateTime,
  getStoreName,
  getWalletName,
  getUserName
}) {
  return (
    <div className="bg-gray-50 border border-gray-100 rounded-3xl p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="font-bold text-gray-900">
            {getTypeIcon(tx.type)} {getTypeLabel(tx.type)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            #{String(tx.id).slice(0, 8)}
          </p>
        </div>

        <p className="text-2xl font-bold text-green-700">
          {formatNumber(tx.amount)}
        </p>
      </div>

      <p className="text-gray-700 text-sm mb-4">
        {tx.description || '-'}
      </p>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Info label="المحل" value={getStoreName(tx.store_id)} />
        <Info label="المحفظة" value={getWalletName(tx.wallet_id)} />
        <Info label="المستخدم" value={getUserName(tx.user_id)} />
        {tx.related_user_id && <Info label="الطرف الآخر" value={getUserName(tx.related_user_id)} />}
        <Info label="التاريخ" value={formatDateTime(tx.created_at)} />
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-3 border border-gray-100">
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="font-bold text-gray-900 truncate">{value}</p>
    </div>
  );
}

export default Reports;
